import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, QrCode, Users, Megaphone, Send, Settings as SettingsIcon, LogOut, Ticket, Star, Globe, Briefcase } from "lucide-react";
import { api, LOGO_URL } from "../lib/api";
import Dashboard from "./Dashboard";
import Campaigns from "./Campaigns";
import CampaignBuilder from "./CampaignBuilder";
import Customers from "./Customers";
import CustomerProfile from "./CustomerProfile";
import Ads from "./Ads";
import Broadcasts from "./Broadcasts";
import Leads from "./Leads";
import Settings from "./Settings";
import Certificates from "./Certificates";
import Reviews from "./Reviews";
import Site from "./Site";
import JobApplications from "./JobApplications";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Дашборд", end: true },
  { to: "/admin/campaigns", icon: QrCode, label: "Кампании / QR" },
  { to: "/admin/customers", icon: Users, label: "Клиенты (CRM)" },
  { to: "/admin/ads", icon: Megaphone, label: "Реклама" },
  { to: "/admin/certificates", icon: Ticket, label: "Сертификаты" },
  { to: "/admin/reviews", icon: Star, label: "Отзывы сайта" },
  { to: "/admin/jobs", icon: Briefcase, label: "Заявки на работу" },
  { to: "/admin/site", icon: Globe, label: "Сайт" },
  { to: "/admin/broadcasts", icon: Send, label: "Рассылки" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Настройки" },
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("craft_token", data.token);
      onLogin(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Ошибка входа");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#FBF3E4] flex items-center justify-center p-5">
      <form onSubmit={submit} className="w-full max-w-[400px] bg-white rounded-3xl p-8 space-y-4 shadow-xl">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="w-12 h-12 rounded-2xl" />
          <div>
            <div className="font-display font-black text-xl">CRAFT Admin</div>
            <div className="text-xs text-gray-400 font-bold">QR Platform</div>
          </div>
        </div>
        <input data-testid="admin-email-input" className="craft-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="admin-password-input" className="craft-input" type="password" placeholder="Пароль" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="admin-login-btn" className="craft-btn" disabled={busy}>Войти</button>
      </form>
    </div>
  );
}

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem("craft_token")) { setChecked(true); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("craft_token")).finally(() => setChecked(true));
  }, []);
  if (!checked) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FEBC10] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  const logout = () => { localStorage.removeItem("craft_token"); setUser(null); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-[#FBF3E4] md:flex">
      <aside className="hidden md:flex flex-col w-60 bg-[#111] text-white p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2 pb-5">
          <img src={LOGO_URL} alt="" className="w-9 h-9 rounded-xl" />
          <div className="font-display font-black">CRAFT <span className="text-[#FEBC10]">Admin</span></div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label}`}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors ${isActive ? "bg-[#FEBC10] text-black" : "text-gray-300 hover:bg-white/10"}`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="text-xs text-gray-500 px-2 pb-2 truncate">{user.email}</div>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-gray-300 hover:bg-white/10"><LogOut size={16} /> Выйти</button>
      </aside>
      <div className="md:hidden bg-[#111] text-white sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2"><img src={LOGO_URL} alt="" className="w-8 h-8 rounded-lg" /><span className="font-display font-black text-sm">CRAFT Admin</span></div>
          <button onClick={logout} className="p-2"><LogOut size={16} /></button>
        </div>
        <div className="flex overflow-x-auto gap-1 px-3 pb-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold ${isActive ? "bg-[#FEBC10] text-black" : "bg-white/10 text-gray-300"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>
      <main className="flex-1 p-4 md:p-6 min-w-0">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/:id" element={<CampaignBuilder />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<CustomerProfile />} />
          <Route path="ads" element={<Ads />} />
          <Route path="certificates" element={<Certificates />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="jobs" element={<JobApplications />} />
          <Route path="site" element={<Site />} />
          <Route path="broadcasts" element={<Broadcasts />} />
          <Route path="leads" element={<Leads />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/admin" />} />
        </Routes>
      </main>
    </div>
  );
}
